import React, { useState, useEffect, useCallback } from 'react';
import { Divider, notification } from 'antd';
import { IOnSubmitArgs, OpForm } from 'components/customAntd/DLS/OpForm/OpForm';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { User } from 'types/userTypes';
import { getRequest, patchRequest, postRequest } from 'api/apiClient';
import { formatFullName, hasPermission } from 'utils/utils';
import { Visitor } from 'types/visitTypes';
import Title from 'antd/es/typography/Title';
import { OpModal } from 'components/customAntd/DLS/OpModal/OpModal';
import Loader from 'components/customAntd/Loader';
import { fetchVisits } from 'store/slices/visitsSlice';
import { SIGNED_IN_VISITOR_ID } from 'constants/userActivities';
import { FormFieldWorkflow, VisitData } from 'types/formFieldTypes';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';

const RequiredFieldsModal: React.FC<{
    open: boolean;
    requiredFields: string[];
    checkedVisitors: Visitor[];
    onClose: () => void;
    updateVisitStatus: () => void;
    updateVisitorStatus: (visitorId: number) => void;
    setDrawerLoading: (loading: boolean) => void;
}> = ({ open, requiredFields, checkedVisitors, onClose, updateVisitStatus, updateVisitorStatus, setDrawerLoading }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const { selectedVisit } = useSelector((state: RootState) => state.visits);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);

    const [hosts, setHosts] = useState<User[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [visitData, setVisitData] = useState<VisitData[]>([]);
    const [fieldsWithoutValue, setFieldsWithoutValue] = useState<FormFieldWorkflow[]>([]);
    const hasAllVisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:w');

    const [form] = OpForm.useForm();

    const { t } = useTranslation();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Parallel fetch for independent data
                const [hostsResponse, locationDefaultWorkflow, locationVisitorTypeWorkflow] = await Promise.all([
                    getRequest(`/orgs/${orgId}/users`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site?.id}/workflows`),
                    getRequest(`/orgs/${orgId}/sites/${selectedVisit?.site.id}/visitorType/${selectedVisit?.visitorType.id}`)
                ]);

                // Process hosts
                const filteredHosts = hostsResponse.data.filter((user: User) => user.status === 1 && (user.hostExclude === 0 || user.hostExclude === null));
                setHosts(filteredHosts);

                // Get workflow ID
                const defaultWorkflowData = locationDefaultWorkflow?.data?.[0];
                const visitorTypeWorkflowData = locationVisitorTypeWorkflow?.data?.[0]?.workflow;

                const signInWorkflowId =
                    visitorTypeWorkflowData?.SignInWorkflowId !== -1
                        ? visitorTypeWorkflowData.SignInWorkflowId
                        : defaultWorkflowData?.SignInWorkflowId;

                // Parallel fetch for workflow fields and visit data
                const [customFieldsWorkflow, visitData] = await Promise.all([
                    getRequest(`/orgs/${orgId}/visitorWorkflow/${signInWorkflowId}/fieldCustom`),
                    getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorData`)
                ]);

                // Filter fields without values
                const fields = customFieldsWorkflow.data.filter((formField: FormFieldWorkflow) => {
                    const matchingVisitData = visitData.data.find(
                        (data: VisitData) => data.formField.id === formField.id && data.value !== null && data.value !== ""
                    );
                    return formField.required === 1 && !matchingVisitData; // Keep only required fields without values
                });

                setVisitData(visitData.data);
                setFieldsWithoutValue(fields);
            } catch (error) {
                console.log("Failed to fetch data.");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [orgId, selectedVisit?.id, selectedVisit?.site.id, selectedVisit?.visitorType.id]);

    const handleOk = async () => {
        form
            .validateFields()
            .then(() => {
                form.submit();
                onClose();
            })
            .catch((errorInfo) => {
                notification.error({
                    message: t('notification.error'),
                    description: t('notification.validationError'),
                    placement: 'bottomRight',
                });
            });
    };


    const renderFormFields = () => {
        return (
            <>
                {fieldsWithoutValue.map((formField, index) => {
                    if (formField.values) {
                        const options = formField.values.map((option) => ({
                            label: option.display || option.name,
                            value: option.value,
                        }));

                        return (
                            <OpForm.Select
                                key={formField.id}
                                label={formField.name}
                                name={formField.id}
                                options={options}
                                rules={[{ required: formField.required === 1 }]}
                                placeholder={formField.placeholder ? formField.placeholder : undefined}
                            />
                        );
                    } else {
                        return (
                            <OpForm.TextAreaInput
                                key={formField.id}
                                label={formField.name}
                                name={formField.id}
                                rows={4}
                                rules={[{ required: formField.required === 1 }]}
                                placeholder={formField.placeholder ? formField.placeholder : undefined}
                            />
                        );
                    }
                })}
            </>
        );
    };

    const handleSubmit = useCallback(async ({ values }: IOnSubmitArgs<any>) => {
        setDrawerLoading(true);
        try {
            // Construct visit payload
            const visitPayload: Record<string, any> = {};
            if (values.company) visitPayload.company = values.company;
            if (values.hostId) visitPayload.hostId = values.hostId;
            if (values.visitPurpose) visitPayload.purpose = values.visitPurpose;

            // Submit visit-level payload
            await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}?language=${i18n.language.split('-')[0]}`, visitPayload);

        // Group fields by visitor and submit each visitor's payload
        const visitorPayloads: Record<string, Record<string, any>> = {};

        Object.entries(values).forEach(([key, value]) => {
            if (key.startsWith("visitor_") && value) {
                // Extract visitor index and field name (e.g., "visitor_0_email")
                const [, index, field] = key.split("_");
                if (!visitorPayloads[index]) visitorPayloads[index] = {};
                visitorPayloads[index][field] = value;
            }
        });

        // Submit payloads for each visitor
        const visitorRequests = Object.entries(visitorPayloads).map(
            async ([index, payload]) => {
                await patchRequest(
                    `/orgs/${orgId}/visitor/${selectedVisit?.id}/visitors/${index}`,
                    payload
                );
            }
        );

        for (const key in values) {
            const field = values[key];
            // Check if the field is a custom field (custom fields have numeric keys)
            const isCustomField = !isNaN(Number(key));

            if (isCustomField) {
                const existingCustomField = visitData.find(
                    (data: VisitData) => Number(data.formField.id) === Number(key)
                );

                // Add field if enabled
                try {
                    if (existingCustomField) {
                        await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorData/${existingCustomField.id}`, {
                            fieldId: key,
                            value: field,
                        });
                    } else {
                        await postRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitorData`, {
                            fieldId: key,
                            value: field,
                        });
                    }
                } catch (error) {
                    console.log(`Failed to add custom field ${key}:`, error);
                }
            }
        }

        await Promise.all(visitorRequests); // Ensure all requests complete

        // update status of visit and visitors
        for (const visitor of checkedVisitors) {
            if (visitor.id) {
                await updateVisitorStatus(visitor.id);
            }
        }
        await updateVisitStatus();

        // log activity
        const fullNameList = checkedVisitors?.map((visitor: Visitor) => formatFullName(visitor.firstName, visitor.middleName, visitor.lastName,)).join(', ');
        await postRequest(`/orgs/${orgId}/userActivity`, {
            userId: globalUserId,
            activityId: SIGNED_IN_VISITOR_ID,
            details: fullNameList
        });
        await dispatch(fetchVisits({ orgId }));
        if (Object.keys(lastSearchParams).length !== 0) {
            await dispatch(searchVisitors({
                ...lastSearchParams,
                orgId,
                siteId: lastSearchParams.siteId || globalLocationId! // 사이트 우선순위 적용
            }));
        }
        await dispatch(searchVisitorsToday({ orgId }));

        form.resetFields();

        console.log("Visitor updates completed.");
        } catch (error) {
            console.error("Failed to submit required fields:", error);
            notification.error({
                message: t('notification.error'),
                description: t('visitors.requiredFieldsModal.submitError'),
                placement: 'bottomRight',
            });
        } finally {
            setDrawerLoading(false);
        }
    }, [orgId, selectedVisit, checkedVisitors, dispatch, form, globalUserId, globalLocationId, lastSearchParams, visitData, updateVisitStatus, updateVisitorStatus, setDrawerLoading, t]);

    const shouldShowVisitInformationTitle =
        (requiredFields.includes('visitCompany') && !selectedVisit?.company) ||
        ((requiredFields.includes('host') || hosts.length > 0) && !selectedVisit?.host) ||
        (requiredFields.includes('visitPurpose') && !selectedVisit?.purpose);

    const hasVisitOrVisitorInfo =
        shouldShowVisitInformationTitle ||
        checkedVisitors.some((visitor) =>
            (requiredFields.includes('middleName') && !visitor.middleName) ||
            (requiredFields.includes('email') && !visitor.email) ||
            (requiredFields.includes('visitPhone') && !visitor.mobilePhone)
        );

    const filteredHosts = hasAllVisitorsWrite
        ? hosts
        : hosts.filter(host => host.id === globalUserId);

    return (
        <OpModal
            open={open}
            title={t('visitors.requiredFieldsModal.title')}
            onOk={handleOk}
            onCancel={loading ? undefined : onClose}
            centered
            closable={!loading}
            footer={loading ? null : undefined}
        >
            {loading ? (
                <Loader />
            ) : (
                <OpForm
                    form={form}
                    onSubmit={handleSubmit}
                    defaultButtons={false}
                    hasError={false}
                    style={{ maxHeight: '70vh', overflowY: 'auto', padding: '8px' }}
                >
                    {shouldShowVisitInformationTitle && (
                        <Title level={5} style={{ marginBottom: 16 }}>
                            {t('visitors.requiredFieldsModal.visitInfo')}
                        </Title>
                    )}
                    {requiredFields.includes('visitCompany') && !selectedVisit?.company && (
                        <OpForm.Input
                            label={t('visitors.requiredFieldsModal.company')}
                            name="company"
                            rules={[{ required: true, message: t('visitors.requiredFieldsModal.companyRequired') }]}
                        />
                    )}
                    {(requiredFields.includes('host') && filteredHosts.length > 0) && selectedVisit?.host.userId === null && (
                        <OpForm.Select
                            label={t('visitors.requiredFieldsModal.host')}
                            name="hostId"
                            rules={[{ required: true, message: t('visitors.requiredFieldsModal.hostRequired') }]}
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                            }
                            placeholder={filteredHosts.length === 0 ? t('visitors.requiredFieldsModal.hostPlaceholderNotAvailable') : t('visitors.requiredFieldsModal.hostPlaceholder')}
                            disabled={filteredHosts.length === 0}
                            options={filteredHosts.map(host => ({
                                label: host.identity?.firstName || host.identity?.lastName
                                    ? formatFullName(host.identity.firstName, host.identity.middleName, host.identity.lastName)
                                    : host.identity?.email,
                                value: host.id
                            }))}
                        />
                    )}
                    {requiredFields.includes('visitPurpose') && !selectedVisit?.purpose && (
                        <OpForm.TextAreaInput
                            label={t('visitors.requiredFieldsModal.purpose')}
                            name="visitPurpose"
                            rules={[{ required: true, message: t('visitors.requiredFieldsModal.purposeRequired') }]}
                            rows={4}
                        />
                    )}
                    {checkedVisitors.some((visitor) =>
                        (requiredFields.includes('middleName') && !visitor.middleName) ||
                        (requiredFields.includes('email') && !visitor.email) ||
                        (requiredFields.includes('visitPhone') && !visitor.mobilePhone)
                    ) && checkedVisitors.map((visitor, index) => (
                        <div key={visitor.id} style={{ marginBottom: 24 }}>
                            {shouldShowVisitInformationTitle || index !== 0 ? <Divider /> : null}
                            <Title level={5} style={{ marginBottom: 16 }}>
                                {formatFullName(visitor.firstName, visitor.middleName, visitor.lastName)}
                            </Title>
                            {requiredFields.includes('middleName') && !visitor.middleName && (
                                <OpForm.Input
                                    label={t('visitors.requiredFieldsModal.middleName')}
                                    name={`visitor_${visitor.id}_middleName`}
                                    rules={[{ required: true, message: t('visitors.requiredFieldsModal.middleNameRequired') }]}
                                />
                            )}
                            {requiredFields.includes('email') && !visitor.email && (
                                <OpForm.Input
                                    label={t('visitors.requiredFieldsModal.email')}
                                    name={`visitor_${visitor.id}_email`}
                                    rules={[
                                        { required: true, message: t('visitors.requiredFieldsModal.emailRequired') },
                                        { type: 'email', message: t('visitors.requiredFieldsModal.emailInvalid') }
                                    ]}
                                />
                            )}
                            {requiredFields.includes('visitPhone') && !visitor.mobilePhone && (
                                <OpForm.Input
                                    label={t('visitors.requiredFieldsModal.phone')}
                                    name={`visitor_${visitor.id}_mobilePhone`}
                                    rules={[{ required: true, message: t('visitors.requiredFieldsModal.phoneRequired') }]}
                                />
                            )}
                        </div>
                    ))}

                    {fieldsWithoutValue.length > 0 && hasVisitOrVisitorInfo && <Divider />}
                    {(fieldsWithoutValue.length > 0) && (
                        <Title level={5} style={{ marginBottom: 16 }}>
                            {t('visitors.requiredFieldsModal.customFields')}
                        </Title>
                    )}
                    {renderFormFields()}
                </OpForm>
            )}
        </OpModal>
    );
};

export default RequiredFieldsModal;